import {parseRunId} from '../../../../apolloClient';
import {getDatadogRum} from '../../../../util/datadog/datadogRum';
import * as Filter from '../../../../util/filters';
import type {Filter as FilterType} from '../../../../util/filterTypes';
import {Key} from '../../../../util/runTypes';

const DD_RUM = getDatadogRum();

/**
 * Calculate the runs query limit accounting for baseline and pinned runs.
 *
 * Args:
 *     isSingleRunMode (boolean): Whether the view targets a single run.
 *     pageSize (number): Desired total rows for the panel before adjustments.
 *     includeBaseline (boolean): Whether the baseline run is fetched separately.
 *     pinnedCount (number): Number of pinned runs fetched outside the main query.
 *     entityName (string, optional): Entity name for error context.
 *     projectName (string, optional): Project name for error context.
 *
 * Returns:
 *     number: The adjusted limit for the main runs query. Can be 0 when baseline
 *             and pinned runs already meet or exceed the page size.
 *
 * Examples:
 *     >>> calculateRunsLimit(true, 25, true, 3)
 *     1  // Single-run mode always returns 1
 *     >>> calculateRunsLimit(false, 10, true, 2)
 *     7  // 10 - 1 baseline - 2 pinned = 7
 *     >>> calculateRunsLimit(false, 2, true, 2)
 *     0  // 2 - 1 baseline - 2 pinned = -1, returns 0 (no regular runs needed)
 */
export function calculateRunsLimit(
  isSingleRunMode: boolean,
  pageSize: number,
  includeBaseline: boolean,
  pinnedCount: number,
  entityName?: string,
  projectName?: string
): number {
  // Input validation
  if (pageSize < 1) {
    DD_RUM.addAction(
      'BaselinePinnedRuns: Invalid pageSize in calculateRunsLimit',
      {
        function: 'calculateRunsLimit',
        pageSize,
        ...(entityName && {entity: entityName}),
        ...(projectName && {project: projectName}),
      }
    );
    return 1;
  }
  if (pinnedCount < 0) {
    DD_RUM.addAction(
      'BaselinePinnedRuns: Invalid pinnedCount in calculateRunsLimit',
      {
        function: 'calculateRunsLimit',
        pinnedCount,
        ...(entityName && {entity: entityName}),
        ...(projectName && {project: projectName}),
      }
    );
    return 1;
  }

  if (isSingleRunMode) {
    return 1;
  }
  // Allow 0 when baseline + pinned already meet/exceed page size
  // Display logic will handle prioritization of which runs to show
  return Math.max(0, pageSize - Number(includeBaseline) - pinnedCount);
}

/**
 * Constructs filter and metadata for baseline run query.
 *
 * Returns an object with:
 * - filters: MongoDB filter JSON string for fetching the baseline run
 * - include: Whether to include the baseline run in the query
 * - runName: The parsed name of the baseline run (or null if not present)
 *
 * Args:
 * - baselineRunId: The encoded GQL ID of the baseline run (may be undefined if no baseline)
 * - isSingleRunMode: Whether viewing a single run (baseline should be excluded)
 * - isGrouped: Whether grouping is applied (baseline should be excluded)
 * - entityName: Entity name for error context (optional)
 * - projectName: Project name for error context (optional)
 */
export function getBaselineRunQueryParams({
  baselineRunId,
  isSingleRunMode,
  isGrouped,
  entityName,
  projectName,
}: {
  baselineRunId: string | undefined;
  isSingleRunMode: boolean;
  isGrouped: boolean;
  entityName?: string;
  projectName?: string;
}) {
  const runName = baselineRunId ? parseRunId({id: baselineRunId}) : null;

  // Skip baseline entirely in single-run mode or when grouped
  if (isSingleRunMode || isGrouped) {
    return {
      filters: undefined,
      include: false,
      runName: null, // null so applyRunExclusions doesn't exclude it
    };
  }

  // Log error if parseRunId failed for a provided baselineRunId
  if (baselineRunId && !runName) {
    DD_RUM.addError(
      new Error('BaselinePinnedRuns: Failed to parse baseline run ID'),
      {
        function: 'getBaselineRunQueryParams',
        baselineRunId,
        ...(entityName && {entity: entityName}),
        ...(projectName && {project: projectName}),
      }
    );
  }

  return {
    filters: runName
      ? JSON.stringify(
          Filter.toMongo({
            op: '=',
            key: {section: 'run', name: 'name'},
            value: runName,
          })
        )
      : undefined,
    include: !!runName,
    runName,
  };
}

/**
 * Constructs filter and metadata for pinned runs query.
 *
 * Returns an object with:
 * - filters: MongoDB filter JSON string for fetching pinned runs
 * - include: Whether to include pinned runs in the query
 * - runNames: Array of parsed run names (limited by page size constraints)
 * - limitedCount: Number of pinned runs that will actually be fetched (after limiting)
 *
 * Args:
 * - pinnedRunIds: Array of encoded GQL IDs of pinned runs (optional)
 * - isSingleRunMode: Whether viewing a single run (pinned should be excluded)
 * - pageSize: Maximum number of runs to display
 * - includeBaseline: Whether baseline is being fetched separately
 * - isGrouped: Whether grouping is applied (pinned should be excluded)
 * - baselineRunName: The name of the baseline run (optional)
 * - entityName: Entity name for error context (optional)
 * - projectName: Project name for error context (optional)
 */
export function getPinnedRunsQueryParams({
  pinnedRunIds,
  isSingleRunMode,
  pageSize,
  includeBaseline,
  isGrouped,
  baselineRunName,
  entityName,
  projectName,
}: {
  pinnedRunIds?: string[];
  isSingleRunMode: boolean;
  pageSize: number;
  includeBaseline: boolean;
  isGrouped: boolean;
  baselineRunName?: string;
  entityName?: string;
  projectName?: string;
}) {
  const parsedResults =
    pinnedRunIds?.map(id => ({
      id,
      name: parseRunId({id}),
    })) ?? [];

  // Log errors for any IDs that failed to parse
  const failedIds = parsedResults.filter(r => r.name == null);
  if (failedIds.length > 0) {
    DD_RUM.addError(
      new Error('BaselinePinnedRuns: Failed to parse pinned run IDs'),
      {
        function: 'getPinnedRunsQueryParams',
        failedIds: failedIds.map(r => r.id),
        failedCount: failedIds.length,
        ...(entityName && {entity: entityName}),
        ...(projectName && {project: projectName}),
      }
    );
  }

  let runNames = parsedResults
    .map(r => r.name)
    .filter((name): name is string => name != null);

  // Exclude baseline run from pinned runs if it's also pinned
  // This prevents double-counting when calculating the limit for regular runs
  if (baselineRunName && runNames.includes(baselineRunName)) {
    runNames = runNames.filter(name => name !== baselineRunName);
  }
  // Limit pinned runs to fit within page size after accounting for baseline
  // Priority: baseline > pinned > regular
  if (!isSingleRunMode && runNames.length > 0) {
    const maxPinnedRuns = Math.max(0, pageSize - Number(includeBaseline));
    if (runNames.length > maxPinnedRuns) {
      runNames = runNames.slice(0, maxPinnedRuns);
    }
  }

  return {
    filters:
      runNames.length > 0
        ? JSON.stringify(
            Filter.toMongo({
              op: 'IN',
              key: {section: 'run', name: 'name'},
              value: runNames,
            })
          )
        : undefined,
    include: !isSingleRunMode && !isGrouped && runNames.length > 0,
    runNames,
    limitedCount: runNames.length,
  };
}

/**
 * Applies exclusions to the base filters to prevent baseline/pinned runs
 * from appearing in the main runs query.
 *
 * In single-run mode, no exclusions are needed (main query already targets specific run).
 * In multi-run mode, excludes baseline and pinned runs to prevent overlap.
 */
export function applyRunExclusions(
  baseFilters: FilterType<Key>,
  options: {
    isSingleRunMode: boolean;
    baselineRunName: string | null;
    pinnedRunNames: string[];
  }
): FilterType<Key> {
  if (options.isSingleRunMode) {
    return baseFilters;
  }

  const exclusionFilters: FilterType<Key>[] = [];

  if (options.baselineRunName) {
    exclusionFilters.push({
      op: '!=',
      key: {section: 'run', name: 'name'},
      value: options.baselineRunName,
    });
  }

  if (options.pinnedRunNames.length > 0) {
    exclusionFilters.push({
      op: 'NIN',
      key: {section: 'run', name: 'name'},
      value: options.pinnedRunNames,
    });
  }

  return exclusionFilters.length > 0
    ? Filter.And([baseFilters, ...exclusionFilters])
    : baseFilters;
}
